// /src/pages/account/EditProduct.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  wooGetProduct,
  wooUpdateProduct,
  wooGetAllCategories,
  wooUpdateProductAttributes,
  WooProduct,
  WooCategory,
  wooGetVariations,
  WooVariation,
  WooProductAttribute,
} from "../../services/woo";
import ProductVariationsManager from "../../components/ProductVariationsManager";
import ProductAttributesManager from "../../components/ProductAttributesManager";
import ProductImageManager from "../../components/ProductImageManager";
import RichTextEditor from "../../components/RichTextEditor";
import { showToast } from "../../assets/util/showToast";

import { BASE_FOLDER, WOO_URL } from "../../config";

type FormState = {
  name: string;
  type: "simple" | "grouped" | "external" | "variable";
  status: "draft" | "pending" | "private" | "publish";
  regular_price: string;
  sale_price: string;
  manage_stock: boolean;
  stock_quantity: string; // keep as string for inputs; convert to number|null on save
  stock_status: "instock" | "outofstock" | "onbackorder";
  short_description: string;
  description: string;
  categoryIds: number[]; // selected category ids
  mainImage: { src: string; alt?: string; name?: string } | null;
  galleryImages: { src: string; alt?: string; name?: string }[];
};

export default function EditProduct() {
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [categories, setCategories] = useState<WooCategory[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [product, setProduct] = useState<WooProduct | null>(null);

  // Variation state
  const [variations, setVariations] = useState<WooVariation[]>([]);
  const [varLoading, setVarLoading] = useState(true);
  const [varError, setVarError] = useState<string | null>(null);

  // Attribute state
  const [attributes, setAttributes] = useState<WooProductAttribute[]>([]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load product + categories + variations
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [p, cats] = await Promise.all([
          wooGetProduct(productId),
          wooGetAllCategories(),
        ]);
        if (!alive) return;

        setProduct(p);
        setCategories(cats);
        setAttributes(p.attributes || []);
        const images = (p as any).images || [];
        setForm({
          name: p.name || "",
          type: p.type || "simple",
          status: p.status || "draft",
          regular_price: p.regular_price || "",
          sale_price: p.sale_price || "",
          manage_stock: p.manage_stock || false,
          stock_quantity: p.stock_quantity?.toString() || "",
          stock_status: p.stock_status || "instock",
          short_description: p.short_description || "",
          description: p.description || "",
          categoryIds: p.categories?.map((c) => c.id) || [],
          mainImage:
            images.length > 0
              ? { src: images[0].src, alt: images[0].alt, name: images[0].name }
              : null,
          galleryImages:
            images.length > 1
              ? images
                  .slice(1)
                  .map((img: any) => ({
                    src: img.src,
                    alt: img.alt,
                    name: img.name,
                  }))
              : [],
        }); // Load variations
        try {
          setVarLoading(true);
          const v = await wooGetVariations(productId);
          if (!alive) return;
          setVariations(v);
          setVarError(null);
        } catch (e: any) {
          if (!alive) return;
          setVarError(e?.message || "Failed to load variations");
        } finally {
          if (alive) setVarLoading(false);
        }

        setError(null);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load product");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [productId]);

  const categoryOptions = useMemo(
    () => categories.sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setHasUnsavedChanges(true);
  }

  // Prevent accidental form submission via Enter key
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    try {
      setSaving(true);
      setError(null);

      // Convert stock_quantity back to number|null
      const sq =
        form.stock_quantity.trim() === ""
          ? null
          : isNaN(Number(form.stock_quantity))
          ? null
          : Number(form.stock_quantity);

      // Prepare images array for WooCommerce
      const images = [];
      if (form.mainImage) {
        images.push({
          src: form.mainImage.src,
          alt: form.mainImage.alt || "",
          name: form.mainImage.name || "",
        });
      }
      if (form.galleryImages && form.galleryImages.length > 0) {
        images.push(
          ...form.galleryImages.map((img) => ({
            src: img.src,
            alt: img.alt || "",
            name: img.name || "",
          }))
        );
      }

      await wooUpdateProduct(productId, {
        name: form.name,
        type: form.type,
        status: form.status,
        regular_price: form.regular_price || "",
        sale_price: form.sale_price || "",
        manage_stock: form.manage_stock,
        stock_quantity: form.manage_stock ? sq : null,
        stock_status: form.manage_stock ? undefined : form.stock_status,
        short_description: form.short_description,
        description: form.description,
        categories: form.categoryIds
          .map((id) => {
            const cat = categories.find((c) => c.id === id);
            return cat ? { id: cat.id, name: cat.name } : undefined;
          })
          .filter(Boolean) as { id: number; name: string }[],
        images: images,
      });

      setHasUnsavedChanges(false);
      showToast("Product saved", "success");
    } catch (e: any) {
      setError(e?.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  }

  const handleAttributesUpdate = async (
    productId: number,
    newAttributes: WooProductAttribute[]
  ) => {
    const updatedProduct = await wooUpdateProductAttributes(
      productId,
      newAttributes
    );
    setProduct(updatedProduct);
    // Attributes are saved immediately, so no unsaved changes
  };

  if (loading) {
    return (
      <div className="page-layout">
        <div className="page-header">
          <h2 className="title">Edit Product</h2>
        </div>
        <div className="card">
          <div className="card-body">Loading…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-layout">
        <div className="page-header">
          <h2 className="title">Edit Product</h2>
        </div>
        <div className="card">
          <div className="card-body text-danger">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!form || !product) return null;

  return (
    <div className="page-layout">
      <div className="page-header">
        <h2 className="title">Edit Product</h2>
        <div className="right">
          <a
            href={WOO_URL + "/product/" + product?.slug}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-primary gap-2 d-flex align-items-center"
          >
            <i className="fa fa-eye"></i>
            <span>View on site</span>
          </a>
          <Link
            to={BASE_FOLDER + `/account/products`}
            className="btn btn-outline-secondary"
          >
            ← Back
          </Link>
        </div>
      </div>

      <div onKeyDown={handleKeyDown}>
        <div className="section-card">
          {/* Image Manager Section */}
          <ProductImageManager
            mainImage={form.mainImage}
            galleryImages={form.galleryImages}
            onMainImageChange={(img) => update("mainImage", img)}
            onGalleryImagesChange={(imgs) => update("galleryImages", imgs)}
          />

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3">
                {/* Name */}
                <div className="col-12 col-md-12">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    required
                  />
                </div>

                {/* Product Type */}
                <div className="col-12 col-md-3">
                  <label className="form-label">Product Type</label>
                  <select
                    className="form-select"
                    value={form.type}
                    onChange={(e) => {
                      const newType = e.target.value as FormState["type"];

                      // Warn when changing from variable to other types if variations exist
                      if (
                        form.type === "variable" &&
                        newType !== "variable" &&
                        variations.length > 0
                      ) {
                        if (
                          window.confirm(
                            `⚠️ Changing from Variable to ${newType} product will hide all variation settings.\n\nExisting variations will remain in the database but won't be accessible until you change back to Variable type.\n\nDo you want to continue?`
                          )
                        ) {
                          update("type", newType);
                        }
                      } else {
                        update("type", newType);
                      }
                    }}
                  >
                    <option value="simple">Simple Product</option>
                    <option value="variable">Variable Product</option>
                    <option value="grouped">Grouped Product</option>
                    <option value="external">External Product</option>
                  </select>
                  <div className="form-text">
                    {form.type === "simple" &&
                      "A simple product with no variations"}
                    {form.type === "variable" &&
                      "A product with multiple variations (size, color, etc.)"}
                    {form.type === "grouped" &&
                      "A collection of related products"}
                    {form.type === "external" &&
                      "A product sold on external website"}
                    {form.type === "variable" && variations.length > 0 && (
                      <span className="text-success">
                        <br />✓ {variations.length} variation(s) configured
                      </span>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="col-12 col-md-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={form.status}
                    onChange={(e) =>
                      update("status", e.target.value as FormState["status"])
                    }
                  >
                    <option value="publish">Publish</option>
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="private">Private</option>
                  </select>
                </div>

                {/* Prices */}
                <div className="col-6 col-md-3">
                  <label className="form-label">Regular Price</label>
                  <input
                    className="form-control"
                    inputMode="decimal"
                    value={form.regular_price}
                    onChange={(e) => update("regular_price", e.target.value)}
                    placeholder="e.g. 199.99"
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label">Sale Price</label>
                  <input
                    className="form-control"
                    inputMode="decimal"
                    value={form.sale_price}
                    onChange={(e) => update("sale_price", e.target.value)}
                    placeholder="e.g. 149.99"
                  />
                </div>

                {/* Stock management - Only for Simple/External products */}
                {form.type !== "variable" && (
                  <>
                    <div className="col-12 col-md-3 d-flex align-items-end gap-2">
                      <div className="form-check">
                        <input
                          id="manageStock"
                          type="checkbox"
                          className="form-check-input"
                          checked={form.manage_stock}
                          onChange={(e) =>
                            update("manage_stock", e.target.checked)
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="manageStock"
                        >
                          Manage stock
                        </label>
                      </div>
                    </div>

                    {/* Stock fields */}
                    {form.manage_stock ? (
                      <div className="col-12 col-md-3">
                        <label className="form-label">Stock Quantity</label>
                        <input
                          className="form-control"
                          inputMode="numeric"
                          value={form.stock_quantity}
                          onChange={(e) =>
                            update("stock_quantity", e.target.value)
                          }
                          placeholder="e.g. 25"
                        />
                      </div>
                    ) : (
                      <div className="col-12 col-md-3">
                        <label className="form-label">Stock Status</label>
                        <select
                          className="form-select"
                          value={form.stock_status}
                          onChange={(e) =>
                            update(
                              "stock_status",
                              e.target.value as FormState["stock_status"]
                            )
                          }
                        >
                          <option value="instock">In stock</option>
                          <option value="outofstock">Out of stock</option>
                          <option value="onbackorder">On backorder</option>
                        </select>
                      </div>
                    )}
                  </>
                )}

                {/* Variable Product Stock Info */}
                {form.type === "variable" && (
                  <div className="col-12 col-md-12">
                    <div className="alert alert-info mb-0">
                      <small>
                        <i className="fa fa-info-circle me-1"></i>
                        Stock is managed at the variation level for variable
                        products.
                      </small>
                    </div>
                  </div>
                )}

                {/* Categories (multi-select) */}
                <div className="col-12 col-md-6">
                  <label className="form-label">Categories</label>
                  <select
                    multiple
                    className="form-select"
                    value={form.categoryIds.map(String)}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map(
                        (o) => Number(o.value)
                      );
                      update("categoryIds", selected);
                    }}
                    style={{ minHeight: 120 }}
                  >
                    {categoryOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    Hold Ctrl/Cmd to select multiple.
                  </div>
                </div>

                {/* Short description */}
                <div className="col-12">
                  <RichTextEditor
                    label="Short Description"
                    value={form.short_description}
                    onChange={(value) => update("short_description", value)}
                    rows={3}
                    placeholder="Enter a brief description of the product..."
                  />
                </div>

                {/* Description */}
                <div className="col-12">
                  <RichTextEditor
                    label="Description"
                    value={form.description}
                    onChange={(value) => update("description", value)}
                    rows={8}
                    placeholder="Enter detailed product description with formatting..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Attributes Manager - Show for variable products */}
          {form.type === "variable" && (
            <div className="mb-3">
              <ProductAttributesManager
                productId={productId}
                attributes={attributes}
                onAttributesChange={setAttributes}
                onUpdate={handleAttributesUpdate}
              />
            </div>
          )}

          {/* Enhanced Variations Manager - Show for variable products */}
          {form.type === "variable" && (
            <div className="mb-3">
              <ProductVariationsManager
                productId={productId}
                variations={variations}
                onVariationsChange={setVariations}
                productAttributes={attributes}
                loading={varLoading}
                error={varError}
              />
            </div>
          )}

          {/* Info for Simple Products */}
          {form.type === "simple" && (
            <div className="mb-3">
              <div className="alert alert-info">
                <h6 className="alert-heading">
                  <i className="fa fa-info-circle me-2"></i>
                  Simple Product
                </h6>
                <p className="mb-0">
                  This is a simple product with no variations. Use the price and
                  stock fields above to manage this product.
                </p>
              </div>
            </div>
          )}

          {/* Info for Other Product Types */}
          {form.type === "grouped" && (
            <div className="mb-3">
              <div className="alert alert-warning">
                <h6 className="alert-heading">
                  <i className="fa fa-cubes me-2"></i>
                  Grouped Product
                </h6>
                <p className="mb-0">
                  Grouped products are a collection of related simple products.
                  You'll need to set up the individual products first, then
                  configure grouping in WooCommerce admin.
                </p>
              </div>
            </div>
          )}

          {form.type === "external" && (
            <div className="mb-3">
              <div className="alert alert-secondary">
                <h6 className="alert-heading">
                  <i className="fa fa-external-link me-2"></i>
                  External/Affiliate Product
                </h6>
                <p className="mb-0">
                  External products are sold on external websites. You'll need
                  to configure the external URL and button text in WooCommerce
                  admin.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="d-flex gap-2">
            <button
              className={`btn ${
                hasUnsavedChanges ? "btn-warning" : "btn-primary"
              }`}
              disabled={saving}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onSave(e as any);
              }}
            >
              {saving
                ? "Saving…"
                : hasUnsavedChanges
                ? "Save changes*"
                : "Save changes"}
            </button>
            {hasUnsavedChanges && (
              <small className="text-warning align-self-center ms-2">
                <i className="fas fa-exclamation-triangle me-1"></i>
                You have unsaved changes
              </small>
            )}
            <Link
              to={`/account/products/${productId}`}
              className="btn btn-outline-secondary"
            >
              Cancel
            </Link>
          </div>

          {error && (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
